import React, { useEffect, useMemo, useState } from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Snackbar from 'components/DesignSystem/Snackbar';
import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useClassicOptions from 'hooks/Flux/Automatic/Classic/useClassicOptions';
import useClassicRevisionList from 'hooks/Flux/Automatic/Classic/useClassicRevisionList';
import sortedDatesAsIntervals from 'helper/Shared/sortedDatesAsIntervals';

import './index.scss';

type SheetSection = 'acomph' | 'forecast' | 'model' | 'revision' | '';

export default function ClassicControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('acomph');

  const {
    acomphDate,
    acomphDateLabel,
    acomphDates,
    forecastDateLabel,
    forecastDates,
    model,
    models,
    setAcomphDate,
    setForecastDateValue,
    setModel,
  } = useClassicOptions();

  const {
    showRevisionList, revisionOptions, selectedRevision, setSelectedRevision,
  } = useClassicRevisionList();

  useEffect(() => {
    if (open) setOpenSection('acomph');
  }, [open]);

  const availableIntervals = useMemo(
    () => sortedDatesAsIntervals(acomphDates),
    [acomphDates],
  );

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="flux-automatic-classic-sheet"
    >
      <div className="flux-automatic-classic-sheet-body">
        <Section
          label="Data da ACOMPH"
          valueText={acomphDateLabel || undefined}
          open={openSection === 'acomph'}
          onToggle={toggleSection('acomph')}
        >
          <DatePicker
            inline
            selected={acomphDate}
            onChange={(date: Date | null) => {
              setAcomphDate(date || new Date());
              setOpenSection('forecast');
            }}
            includeDateIntervals={availableIntervals}
          />
        </Section>

        <Section
          label="Data da Simulação"
          valueText={forecastDateLabel || undefined}
          open={openSection === 'forecast'}
          onToggle={toggleSection('forecast')}
        >
          {forecastDates.length === 0 && (
            <Snackbar className="mt-2">Selecione a data da ACOMPH primeiro.</Snackbar>
          )}
          {forecastDates.map((date) => (
            <OptionRow
              key={date}
              label={date}
              selected={date === forecastDateLabel}
              onSelect={() => {
                setForecastDateValue(date);
                setOpenSection('model');
              }}
            />
          ))}
        </Section>

        <Section
          label="Modelo"
          valueText={model || undefined}
          open={openSection === 'model'}
          onToggle={toggleSection('model')}
        >
          {models.length === 0 && (
            <Snackbar className="mt-2">Selecione a data da simulação primeiro.</Snackbar>
          )}
          {models.map((option) => (
            <OptionRow
              key={option}
              label={option}
              selected={option === model}
              onSelect={() => {
                setModel(option);
                setOpenSection(showRevisionList ? 'revision' : '');
              }}
            />
          ))}
        </Section>

        {showRevisionList && (
          <Section
            label="Revisão"
            valueText={selectedRevision || undefined}
            open={openSection === 'revision'}
            onToggle={toggleSection('revision')}
          >
            {revisionOptions.map((option) => (
              <OptionRow
                key={option}
                label={option}
                selected={option === selectedRevision}
                onSelect={() => {
                  setSelectedRevision(option);
                  setOpenSection('');
                }}
              />
            ))}
          </Section>
        )}
      </div>

      <div className="flux-automatic-classic-sheet-footer">
        <SheetActionButton variant="confirm" onClick={dismiss}>
          Aplicar filtros
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
